import React, { useState, useEffect } from 'react';
import './RoomDetail.css';
import {
  mergePlayersForDisplay,
} from '../utils/mergePlayersForDisplay';
import {
  aggregateSavedGameStats,
  isDrinkBasedGame,
  questionsPlayedFromGameStats,
  scoreUnitLabel,
} from '../utils/adminRoomStats';
import RoomBrandingPanel from './RoomBrandingPanel';
import type { AiPromptUsage, CompanyQuestion, PremiumPurchase, RoomBranding } from '../types';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

interface Player {
  id: string;
  name: string;
  score: number;
  avatar?: string;
  joinedAt: number;
  isHost?: boolean;
  connected?: boolean;
}

interface CurrentQuestion {
  id?: string;
  questionId?: string;
  question: string;
  options?: string[];
  correctAnswer?: string;
  imageUrl?: string;
  questionNumber?: number;
  totalQuestions?: number;
  interesting?: string;
}

interface GameEvent {
  type: 'game_started' | 'game_ended' | 'player_joined' | 'player_left';
  timestamp: number;
  gameType?: string;
  questionNumber?: number;
  totalQuestions?: number;
  reason?: string;
  playerName?: string;
  leaderboard?: Array<{ playerId?: string; playerName: string; score: number }>;
}

interface RoomData {
  roomName: string;
  gameType: string;
  hostId: string;
  players: Player[];
  gameState?: 'waiting' | 'playing' | 'finished'; // Optional for historical rooms
  settings?: {
    timerDuration?: number;
    category?: string;
    whosPlaying?: string;
    numberOfQuestions?: number;
  };
  createdAt?: number;
  gamesPlayed?: number;
  questionsPlayed?: number;
  currentQuestion?: CurrentQuestion;
  questionStartTime?: number;
  gameHistory?: GameEvent[];
  destroyedAt?: number; // For historical rooms
  stateEnteredAt?: number;
  hostDisconnectedAt?: number | null;
  connectedPlayerCount?: number;
  isStalePlaying?: boolean;
  branding?: RoomBranding;
  companyQuestions?: CompanyQuestion[];
  aiPromptUsage?: AiPromptUsage;
}

interface QuestionFeedbackEntry {
  id: string;
  questionId: string;
  roomName: string;
  gameType: string;
  playerId?: string;
  playerName: string;
  rating: number | null;
  liked: boolean | null;
  correct: boolean | null;
  timestamp: number;
  questionText?: string;
  correctAnswer?: string;
}

interface RoundFeedbackEntry {
  id: string;
  roundId: string;
  roomName: string;
  gameType: string;
  playerId?: string;
  playerName: string;
  message: string;
  timestamp: number;
  questionId?: string;
  questionText?: string;
  bonusGameType?: string;
}

interface GameStatisticsData {
  gameId: string;
  roomName: string;
  gameType: string;
  playedAt: number;
  leaderboard: Array<{ playerId: string; playerName: string; score: number }>;
  statistics: {
    totalQuestions: number;
    questionsAnswered?: number;
    averageScore: number;
    mostCorrectAnswers?: { playerId: string; playerName: string; count: number };
    hotTakesStats?: {
      totalQuestions: number;
      totalTies: number;
      mostDrinks: { playerId: string; playerName: string; count: number } | null;
      mostControversial: { statement: string; agreeCount: number; disagreeCount: number } | null;
      playerDrinks: Array<{ playerId: string; playerName: string; count: number }>;
    };
    hotTakesResults?: Array<{
      questionId: string;
      statement: string;
      agreeCount: number;
      disagreeCount: number;
      minorityVote: 'AGREE' | 'DISAGREE' | 'tie';
    }>;
  };
}

interface RoomStatisticsResponse {
  roomName: string;
  games: GameStatisticsData[];
  totalGames: number;
}

interface RoomDetailProps {
  room: RoomData;
  premiumPurchase?: PremiumPurchase | null;
  onBack: () => void;
  onRefresh: () => void;
  isRefreshing: boolean;
  onKillRoom: (roomName: string) => void;
  socket?: { emit: (event: string, data: any) => void; on: (event: string, callback: (data: any) => void) => void; off?: (event: string, callback?: (data: any) => void) => void } | null;
  refreshToken?: number;
}

const GAME_TYPE_LABELS: Record<string, string> = {
  'trivia': 'Trivia',
  'speed-challenge': 'Speed Challenge',
  'collaborative-puzzle': 'Collaborative Puzzle',
  'story-chain': 'Story Chain',
  'word-puzzle': 'Word Puzzle',
  'close-or-not': 'Close or Not',
  'picture-perfect': 'Picture Perfect',
  'drinking-game': 'Drinking Game',
  'hot-takes': 'Hot Takes',
  'would-you-rather': 'Would You Rather',
  'odd-one-out': 'Odd One Out',
  'estimate-it': 'Estimate It',
  'true-or-silly': 'True or Silly?',
};

const STATE_CONFIGS: Record<string, { color: string; label: string; icon: string }> = {
  waiting: { color: 'var(--accent-warning)', label: 'Waiting for players', icon: '⏳' },
  playing: { color: 'var(--accent-primary)', label: 'Game in progress', icon: '🎮' },
  finished: { color: 'var(--accent-secondary)', label: 'Game finished', icon: '🏆' },
  destroyed: { color: 'var(--text-muted)', label: 'Room ended', icon: '📜' },
};

// Helper to get displayable avatar (handles SVG paths from main app)
const getDisplayAvatar = (avatar?: string): string => {
  if (!avatar) return '👤';
  // If it's a path (starts with / or http or contains .svg), use default emoji
  if (avatar.startsWith('/') || avatar.startsWith('http') || avatar.includes('.svg')) {
    return '👤';
  }
  return avatar;
};

const RoomDetail: React.FC<RoomDetailProps> = ({ room, premiumPurchase, onBack, onRefresh, isRefreshing, onKillRoom, socket, refreshToken }) => {
  // Historical rooms might not have gameState, default to 'destroyed'
  const gameState = room.gameState || (room.destroyedAt ? 'destroyed' : 'waiting');
  const baseStateConfig = STATE_CONFIGS[gameState] || STATE_CONFIGS.destroyed;
  const stateConfig = room.isStalePlaying
    ? {
        color: 'var(--text-muted)',
        label: 'Stale playing (abandoned — auto-kill after 1h)',
        icon: '💤',
      }
    : baseStateConfig;
  const onlineCount =
    typeof room.connectedPlayerCount === 'number'
      ? room.connectedPlayerCount
      : room.players.filter((p) => p.connected !== false).length;
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [now, setNow] = useState(Date.now());
  const [roomFeedback, setRoomFeedback] = useState<QuestionFeedbackEntry[]>([]);
  const [roundFeedback, setRoundFeedback] = useState<RoundFeedbackEntry[]>([]);
  const [roomStatistics, setRoomStatistics] = useState<RoomStatisticsResponse | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [localBranding, setLocalBranding] = useState<RoomBranding | undefined>(room.branding);
  const [localQuestions, setLocalQuestions] = useState<CompanyQuestion[]>(room.companyQuestions ?? []);
  const [localAiUsage, setLocalAiUsage] = useState<AiPromptUsage | null | undefined>(room.aiPromptUsage);
  const [brandingModalOpen, setBrandingModalOpen] = useState(false);
  const [clearingRoom, setClearingRoom] = useState(false);

  useEffect(() => {
    setLocalBranding(room.branding);
    setLocalQuestions(room.companyQuestions ?? []);
    setLocalAiUsage(room.aiPromptUsage);
  }, [room.roomName, room.branding, room.companyQuestions, room.aiPromptUsage, refreshToken]);

  useEffect(() => {
    if (!socket?.on) return;
    const handleClearResult = (data: { success: boolean; error?: string; room?: RoomData }) => {
      setClearingRoom(false);
      if (!data.success) {
        window.alert(
          data.error === 'GAME_IN_PROGRESS'
            ? 'Cannot clear while a game is in progress. Wait until lobby/finished.'
            : `Failed to clear room: ${data.error || 'unknown error'}`
        );
        return;
      }
      onRefresh();
    };
    socket.on('admin-clear-room-history-result', handleClearResult);
    return () => {
      socket.off?.('admin-clear-room-history-result', handleClearResult);
    };
  }, [socket, onRefresh]);

  // Reset image state when question changes
  useEffect(() => {
    setImageError(false);
    setImageLoading(true);
  }, [room.currentQuestion?.imageUrl]);

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!socket?.emit) return;
    socket.emit('admin-get-question-feedback', { roomName: room.roomName });
    const handleData = (data: { entries: QuestionFeedbackEntry[]; roomName?: string }) => {
      if (data.roomName === room.roomName || !data.roomName) {
        setRoomFeedback(data.entries || []);
      }
    };
    socket.on('admin-question-feedback-data', handleData);
    return () => {
      if (socket.off) socket.off('admin-question-feedback-data', handleData);
    };
  }, [room.roomName, socket, refreshToken]);

  useEffect(() => {
    if (!socket?.emit) return;
    socket.emit('admin-get-round-feedback', { roomName: room.roomName });
    const handleData = (data: { entries: RoundFeedbackEntry[]; roomName?: string }) => {
      if (data.roomName === room.roomName || !data.roomName) {
        setRoundFeedback(data.entries || []);
      }
    };
    socket.on('admin-round-feedback-data', handleData);
    return () => {
      if (socket.off) socket.off('admin-round-feedback-data', handleData);
    };
  }, [room.roomName, socket, refreshToken]);

  useEffect(() => {
    let isCancelled = false;

    const fetchRoomStatistics = async () => {
      setStatsLoading(true);
      try {
        const response = await fetch(`${BACKEND_URL}/api/rooms/${encodeURIComponent(room.roomName)}/statistics`);
        if (!response.ok) {
          throw new Error(`Failed to fetch statistics: ${response.status}`);
        }
        const payload = (await response.json()) as RoomStatisticsResponse;
        if (!isCancelled) {
          setRoomStatistics(payload);
        }
      } catch (error) {
        if (!isCancelled) {
          setRoomStatistics(null);
        }
      } finally {
        if (!isCancelled) {
          setStatsLoading(false);
        }
      }
    };

    fetchRoomStatistics();
    return () => {
      isCancelled = true;
    };
  }, [room.roomName, refreshToken]);
  
  const formatDuration = (ms: number): string => {
    const seconds = Math.floor(ms / 1000);
    return `${seconds}s`;
  };

  const formatTimestamp = (timestamp: number): string => {
    return new Date(timestamp).toLocaleTimeString();
  };

  const formatElapsed = (ms: number): string => {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  };

  const formatPremiumTimeLeft = (expiresAt: number): string => {
    const ms = Math.max(0, expiresAt - now);
    const hours = Math.floor(ms / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m`;
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${seconds}s`;
  };

  const statisticsGames = roomStatistics?.games || [];
  const savedStats = aggregateSavedGameStats(statisticsGames);
  const hasSavedStats = statisticsGames.length > 0;
  const displayedPlayers = mergePlayersForDisplay(room.players, statisticsGames, room.hostId);
  const sortedPlayers = [...displayedPlayers].sort((a, b) => b.score - a.score);
  const fallbackGamesPlayed =
    room.gamesPlayed !== undefined
      ? room.gamesPlayed
      : (room.gameHistory || []).filter((e) => e.type === 'game_started').length;
  const displayGamesPlayed = hasSavedStats ? savedStats.gamesPlayed : fallbackGamesPlayed;
  const displayQuestionsPlayed = hasSavedStats
    ? savedStats.questionsPlayed
    : room.questionsPlayed !== undefined
      ? room.questionsPlayed
      : null;
  const displayTotalPoints = hasSavedStats
    ? savedStats.totalPoints
    : displayedPlayers.reduce((sum, p) => sum + p.score, 0);
  const displayAverageScore = hasSavedStats
    ? savedStats.averageScore
    : displayedPlayers.length > 0
      ? Math.round(displayedPlayers.reduce((sum, p) => sum + p.score, 0) / displayedPlayers.length)
      : 0;
  const waitingDurationMs = gameState === 'waiting' && room.stateEnteredAt ? now - room.stateEnteredAt : null;
  const staleDurationMs = room.hostDisconnectedAt ? now - room.hostDisconnectedAt : null;
  const latestSavedGame = statisticsGames[0] ?? null;
  const roomScoreIsDrinks =
    isDrinkBasedGame(room.gameType) ||
    (statisticsGames.length > 0 && statisticsGames.every((g) => isDrinkBasedGame(g.gameType)));
  const sessionScoreLabel = scoreUnitLabel(
    roomScoreIsDrinks ? (latestSavedGame?.gameType || room.gameType) : room.gameType,
    statisticsGames.length > 0
  );
  const activityFromStats: GameEvent[] =
    (!room.gameHistory || room.gameHistory.length === 0) && statisticsGames.length > 0
      ? statisticsGames
          .slice()
          .reverse()
          .flatMap((game) => [
            {
              type: 'game_started' as const,
              timestamp: game.playedAt - 1000,
              gameType: game.gameType,
              totalQuestions: questionsPlayedFromGameStats(game.statistics),
            },
            {
              type: 'game_ended' as const,
              timestamp: game.playedAt,
              gameType: game.gameType,
              questionNumber: questionsPlayedFromGameStats(game.statistics),
              totalQuestions: questionsPlayedFromGameStats(game.statistics),
              reason: 'completed',
              leaderboard: game.leaderboard,
            },
          ])
      : [];
  const activityEvents = [
    ...(room.gameHistory || []),
    ...activityFromStats,
  ].sort((a, b) => a.timestamp - b.timestamp);
  
  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  const handleKillClick = () => {
    if (window.confirm(`Kill room ${room.roomName}? This will disconnect all players.`)) {
      onKillRoom(room.roomName);
    }
  };

  const handleClearRoomClick = () => {
    if (!socket?.emit) {
      window.alert('Not connected');
      return;
    }
    if (
      !window.confirm(
        `Clear room ${room.roomName}? This removes game history, saved game stats, and resets scores. Players stay in the room.`
      )
    ) {
      return;
    }
    setClearingRoom(true);
    socket.emit('admin-clear-room-history', { roomName: room.roomName });
  };

  return (
    <div className="room-detail">
      {/* Navigation Bar */}
      <div className="detail-nav">
        <button className="back-button" onClick={onBack}>
          <span className="back-arrow">←</span>
          Back to Rooms
        </button>
        <div className="detail-nav-actions">
          <button className="refresh-button-detail" onClick={onRefresh} disabled={isRefreshing}>
            <span className={`refresh-icon ${isRefreshing ? 'spinning' : ''}`}>↻</span>
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </button>
          <button
            type="button"
            className="branding-open-button"
            onClick={() => setBrandingModalOpen(true)}
          >
            Branding
          </button>
          {!room.destroyedAt && (
            <button
              type="button"
              className="clear-room-button"
              onClick={handleClearRoomClick}
              disabled={clearingRoom || gameState === 'playing'}
              title={gameState === 'playing' ? 'Finish or stop the game before clearing' : undefined}
            >
              {clearingRoom ? 'Clearing…' : 'Clear room'}
            </button>
          )}
          {!room.destroyedAt && (
            <button className="kill-button" onClick={handleKillClick}>
              Kill Room
            </button>
          )}
        </div>
      </div>

      {/* Room Header */}
      <div className="detail-header">
        <div className="header-info">
          <div className="room-code-large">
            <span className="code-prefix">#</span>
            {room.roomName}
          </div>
          <div className="status-badge" style={{ borderColor: stateConfig.color }}>
            <span className="status-icon">{stateConfig.icon}</span>
            <span className="status-text" style={{ color: stateConfig.color }}>
              {stateConfig.label}
            </span>
          </div>
        </div>
        <div className="game-info">
          <div className="game-type-badge">
            {GAME_TYPE_LABELS[room.gameType] || room.gameType}
          </div>
        </div>
      </div>

      {premiumPurchase && (
        <div className="detail-premium-block" style={{ marginBottom: '1rem', padding: '0.75rem 1rem', background: 'var(--bg-secondary, #f5f5f5)', borderRadius: '8px' }}>
          <div className="premium-package">
            <strong>Package:</strong> {premiumPurchase.packageName} ({premiumPurchase.duration} access)
          </div>
          {(premiumPurchase.startsAt != null || premiumPurchase.purchasedAt != null) && (
            <div className="premium-time-left">
              <strong>Window:</strong>{' '}
              {new Date(premiumPurchase.startsAt ?? premiumPurchase.purchasedAt).toLocaleDateString()}
              {' → '}
              {new Date(premiumPurchase.expiresAt).toLocaleDateString()}
            </div>
          )}
          <div className="premium-time-left">
            {(() => {
              const startsAt = premiumPurchase.startsAt ?? premiumPurchase.purchasedAt;
              const notStarted = Date.now() < startsAt;
              return (
                <>
                  <strong>{notStarted ? 'Starts in:' : 'Left:'}</strong>{' '}
                  {formatPremiumTimeLeft(notStarted ? startsAt : premiumPurchase.expiresAt)}
                </>
              );
            })()}
          </div>
          {premiumPurchase.sessionId?.startsWith('promo-') && (
            <div className="premium-email-history" style={{ marginTop: '0.75rem' }}>
              <strong>Promo emails:</strong>
              {(premiumPurchase.promoEmailSends?.length ?? 0) === 0 ? (
                <div style={{ marginTop: '0.35rem', opacity: 0.7 }}>No email sent yet</div>
              ) : (
                <ul style={{ margin: '0.35rem 0 0', paddingLeft: '1.1rem' }}>
                  {[...(premiumPurchase.promoEmailSends ?? [])]
                    .slice()
                    .reverse()
                    .map((send, index) => (
                      <li key={`${send.email}-${send.sentAt}-${index}`}>
                        {send.firstName} &lt;{send.email}&gt; —{' '}
                        {new Date(send.sentAt).toLocaleString()}
                      </li>
                    ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {brandingModalOpen && (
        <RoomBrandingPanel
          roomName={room.roomName}
          branding={localBranding}
          companyQuestions={localQuestions}
          aiPromptUsage={localAiUsage}
          isLiveRoom={!room.destroyedAt && !!room.hostId}
          gameState={gameState}
          socket={socket}
          onClose={() => setBrandingModalOpen(false)}
          onUpdated={(payload) => {
            if (payload.branding !== undefined) {
              setLocalBranding(payload.branding ?? undefined);
            }
            if (payload.companyQuestions !== undefined) {
              setLocalQuestions(payload.companyQuestions);
            }
            if (payload.aiPromptUsage) {
              setLocalAiUsage(payload.aiPromptUsage);
            }
          }}
        />
      )}

      {/* Content Grid */}
      <div className="detail-grid">
        {/* Players Panel */}
        <div className="detail-panel players-panel">
          <div className="panel-header">
            <h3 className="panel-title">
              <span className="title-icon">👥</span>
              {room.destroyedAt
                ? `Players (${displayedPlayers.length})`
                : `Players (${onlineCount} online${
                    displayedPlayers.length !== onlineCount
                      ? ` / ${displayedPlayers.length} listed`
                      : ''
                  })`}
            </h3>
          </div>
          <div className="panel-content">
            {sortedPlayers.length === 0 ? (
              <div className="no-players">
                <span className="no-players-icon">🕳️</span>
                <span>No players yet</span>
              </div>
            ) : (
              <div className="players-list">
                {sortedPlayers.map((player, index) => (
                  <div key={`${player.id}-${player.name}-${index}`} className="player-row">
                    <div className="player-rank">
                      {gameState === 'playing' || gameState === 'finished' ? (
                        <span className={`rank-badge rank-${index + 1}`}>
                          {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`}
                        </span>
                      ) : (
                        <span className="rank-badge">{index + 1}</span>
                      )}
                    </div>
                    <div className="player-avatar">{getDisplayAvatar(player.avatar)}</div>
                    <div className="player-info">
                      <span className="player-name">
                        {player.name}
                        {player.isHost && (
                          <span className="host-badge">HOST</span>
                        )}
                      </span>
                      <span className="player-joined">
                        Joined {formatTimestamp(player.joinedAt)}
                      </span>
                    </div>
                    <div className="player-score">
                      <span className="score-value">{player.score}</span>
                      <span className="score-label">{sessionScoreLabel}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Settings Panel */}
        <div className="detail-panel settings-panel">
          <div className="panel-header">
            <h3 className="panel-title">
              <span className="title-icon">⚙️</span>
              Game Settings
            </h3>
          </div>
          <div className="panel-content">
            <div className="settings-grid">
              <div className="setting-item">
                <span className="setting-label">Timer Duration</span>
                <span className="setting-value">
                  {room.settings?.timerDuration 
                    ? formatDuration(room.settings.timerDuration)
                    : '45s'}
                </span>
              </div>
              <div className="setting-item">
                <span className="setting-label">Category</span>
                <span className="setting-value">
                  {room.settings?.category || 'All'}
                </span>
              </div>
              <div className="setting-item">
                <span className="setting-label">Who's Playing</span>
                <span className="setting-value">
                  {room.settings?.whosPlaying || 'All'}
                </span>
              </div>
              <div className="setting-item">
                <span className="setting-label">Questions</span>
                <span className="setting-value">
                  {room.settings?.numberOfQuestions || 20}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Panel */}
        <div className="detail-panel stats-panel">
          <div className="panel-header">
            <h3 className="panel-title">
              <span className="title-icon">📊</span>
              Room Stats
            </h3>
          </div>
          <div className="panel-content">
            <div className="stats-grid">
              <div className="stat-item">
                <div className="stat-value-large">
                  {displayedPlayers.length}
                </div>
                <div className="stat-label">{hasSavedStats ? 'Unique Players' : 'Players'}</div>
              </div>
              <div className="stat-item">
                <div className="stat-value-large">
                  {displayTotalPoints}
                </div>
                <div className="stat-label">{hasSavedStats ? (roomScoreIsDrinks ? 'Total Drinks (All Games)' : 'Total Points (All Games)') : (roomScoreIsDrinks ? 'Total Drinks' : 'Total Points')}</div>
              </div>
              <div className="stat-item">
                <div className="stat-value-large">
                  {displayGamesPlayed}
                </div>
                <div className="stat-label">Games Played</div>
              </div>
              <div className="stat-item">
                <div className="stat-value-large">
                  {displayQuestionsPlayed !== null ? displayQuestionsPlayed : '—'}
                </div>
                <div className="stat-label">Questions Played</div>
              </div>
              <div className="stat-item">
                <div className="stat-value-large">
                  {displayAverageScore ?? '—'}
                </div>
                <div className="stat-label">{roomScoreIsDrinks ? 'Avg Drinks' : 'Avg Score'}</div>
              </div>
            </div>
            {(waitingDurationMs !== null || staleDurationMs !== null || (room.createdAt && !room.destroyedAt)) && (
              <div className="room-timestamps timing-section">
                {room.createdAt && !room.destroyedAt && (
                  <div className="timestamp-item">
                    <span className="timestamp-label">Created:</span>
                    <span className="timestamp-value">{new Date(room.createdAt).toLocaleString()}</span>
                  </div>
                )}
                {waitingDurationMs !== null && (
                  <div className="timestamp-item">
                    <span className="timestamp-label">Waiting For:</span>
                    <span className="timestamp-value">{formatElapsed(waitingDurationMs)}</span>
                  </div>
                )}
                {staleDurationMs !== null && (
                  <div className="timestamp-item">
                    <span className="timestamp-label">Stale For:</span>
                    <span className="timestamp-value">{formatElapsed(staleDurationMs)}</span>
                  </div>
                )}
              </div>
            )}
            {room.destroyedAt && (
              <div className="room-timestamps" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border-color)' }}>
                <div className="timestamp-item">
                  <span className="timestamp-label">Created:</span>
                  <span className="timestamp-value">
                    {room.createdAt ? new Date(room.createdAt).toLocaleString() : 'Unknown'}
                  </span>
                </div>
                <div className="timestamp-item">
                  <span className="timestamp-label">Destroyed:</span>
                  <span className="timestamp-value">
                    {new Date(room.destroyedAt).toLocaleString()}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Room Statistics History */}
        <div className="detail-panel history-stats-panel">
          <div className="panel-header">
            <h3 className="panel-title">
              <span className="title-icon">📈</span>
              Games History
              {!statsLoading && statisticsGames.length > 0 && (
                <span className="question-counter">{statisticsGames.length} games</span>
              )}
            </h3>
          </div>
          <div className="panel-content">
            {statsLoading ? (
              <div className="no-question">
                <span className="no-question-icon">⏳</span>
                <span>Loading statistics…</span>
              </div>
            ) : statisticsGames.length === 0 ? (
              <div className="no-question">
                <span className="no-question-icon">📭</span>
                <span>
                  {displayGamesPlayed > 0
                    ? 'Games ran here, but no saved statistics were found (may have expired or never finished cleanly).'
                    : 'No saved game statistics yet — finish a full game to populate this.'}
                </span>
              </div>
            ) : (
              <div className="stats-history-list">
                {statisticsGames.map((game) => {
                  const drinkGame = isDrinkBasedGame(game.gameType);
                  const drinkStats = game.statistics.hotTakesStats;
                  const unit = scoreUnitLabel(game.gameType);
                  const highlightName = drinkGame
                    ? drinkStats?.mostDrinks?.playerName || game.leaderboard[0]?.playerName
                    : game.statistics.mostCorrectAnswers?.playerName || game.leaderboard[0]?.playerName;
                  const highlightLabel = drinkGame ? 'Most Drinks' : 'Leader';
                  return (
                  <div key={game.gameId} className="stats-history-card">
                    <div className="stats-history-header">
                      <span className="stats-history-game">{GAME_TYPE_LABELS[game.gameType] || game.gameType}</span>
                      <span className="stats-history-time">{new Date(game.playedAt).toLocaleString()}</span>
                    </div>
                    <div className="stats-history-grid">
                      <div className="stat-item">
                        <div className="stat-value-large">{questionsPlayedFromGameStats(game.statistics)}</div>
                        <div className="stat-label">Questions</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-value-large">{Math.round(game.statistics.averageScore || 0)}</div>
                        <div className="stat-label">{drinkGame ? 'Avg Drinks' : 'Avg Score'}</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-value-large">{game.leaderboard.length}</div>
                        <div className="stat-label">Players</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-value-large">
                          {highlightName || '—'}
                        </div>
                        <div className="stat-label">{highlightLabel}</div>
                      </div>
                    </div>
                    {drinkGame && drinkStats?.mostControversial && (
                      <div className="stats-history-note">
                        Most split: “{drinkStats.mostControversial.statement}” (
                        {drinkStats.mostControversial.agreeCount}/
                        {drinkStats.mostControversial.disagreeCount})
                      </div>
                    )}
                    <div className="stats-history-leaderboard">
                      {game.leaderboard.slice(0, 5).map((player, index) => (
                        <div key={`${game.gameId}-${player.playerId}-${index}`} className="stats-leader-row">
                          <span className="stats-leader-rank">{index + 1}.</span>
                          <span className="stats-leader-name">{player.playerName}</span>
                          <span className="stats-leader-score">{player.score} {unit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Current Question / Last Game Panel */}
        <div className="detail-panel question-panel">
          <div className="panel-header">
            <h3 className="panel-title">
              <span className="title-icon">❓</span>
              {gameState === 'playing' ? 'Current Question' : hasSavedStats ? 'Last Game Summary' : 'Current Question'}
              {room.currentQuestion?.questionNumber && room.currentQuestion?.totalQuestions && gameState === 'playing' && (
                <span className="question-counter">
                  {room.currentQuestion.questionNumber} / {room.currentQuestion.totalQuestions}
                </span>
              )}
            </h3>
          </div>
          <div className="panel-content">
            {gameState === 'playing' && room.currentQuestion ? (
              <div className="question-content">
                {/* Question Image */}
                {room.currentQuestion.imageUrl && (
                  <div className="question-image-container">
                    {imageLoading && !imageError && (
                      <div className="image-loading">Loading image...</div>
                    )}
                    {imageError ? (
                      <div className="image-error">
                        <span className="error-icon">❌</span>
                        <span className="error-text">Image failed to load</span>
                        <code className="error-url">{room.currentQuestion.imageUrl}</code>
                      </div>
                    ) : (
                      <img
                        src={room.currentQuestion.imageUrl}
                        alt="Question"
                        className={`question-image ${imageLoading ? 'loading' : ''}`}
                        onLoad={handleImageLoad}
                        onError={handleImageError}
                      />
                    )}
                    {!imageError && !imageLoading && (
                      <div className="image-status success">
                        <span>✅ Image loaded</span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Question Text */}
                <div className="question-text">
                  {room.currentQuestion.question}
                </div>

                {/* Answer Options */}
                {room.currentQuestion.options && room.currentQuestion.options.length > 0 && (
                  <div className="question-options">
                    {room.currentQuestion.options.map((option, index) => (
                      <div
                        key={index}
                        className={`option ${option === room.currentQuestion?.correctAnswer ? 'correct' : ''}`}
                      >
                        <span className="option-letter">{String.fromCharCode(65 + index)}</span>
                        <span className="option-text">{option}</span>
                        {option === room.currentQuestion?.correctAnswer && (
                          <span className="correct-badge">✓</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Fun Fact */}
                {room.currentQuestion.interesting && (
                  <div className="question-fact">
                    <span className="fact-icon">💡</span>
                    <span className="fact-text">{room.currentQuestion.interesting}</span>
                  </div>
                )}
              </div>
            ) : latestSavedGame ? (
              <div className="question-content last-game-summary">
                <div className="question-text">
                  {GAME_TYPE_LABELS[latestSavedGame.gameType] || latestSavedGame.gameType}
                </div>
                <div className="stats-history-grid" style={{ marginTop: '1rem' }}>
                  <div className="stat-item">
                    <div className="stat-value-large">{questionsPlayedFromGameStats(latestSavedGame.statistics)}</div>
                    <div className="stat-label">Questions</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-value-large">
                      {latestSavedGame.leaderboard.reduce((sum, e) => sum + e.score, 0)}
                    </div>
                    <div className="stat-label">
                      {isDrinkBasedGame(latestSavedGame.gameType) ? 'Total Drinks' : 'Total Points'}
                    </div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-value-large">{latestSavedGame.leaderboard.length}</div>
                    <div className="stat-label">Players</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-value-large">
                      {isDrinkBasedGame(latestSavedGame.gameType)
                        ? (latestSavedGame.statistics.hotTakesStats?.mostDrinks?.playerName || latestSavedGame.leaderboard[0]?.playerName || '—')
                        : (latestSavedGame.statistics.mostCorrectAnswers?.playerName || latestSavedGame.leaderboard[0]?.playerName || '—')}
                    </div>
                    <div className="stat-label">
                      {isDrinkBasedGame(latestSavedGame.gameType) ? 'Most Drinks' : 'Leader'}
                    </div>
                  </div>
                </div>
                <div className="stats-history-leaderboard" style={{ marginTop: '0.75rem' }}>
                  {latestSavedGame.leaderboard.slice(0, 5).map((player, index) => (
                    <div key={`last-${player.playerId}-${index}`} className="stats-leader-row">
                      <span className="stats-leader-rank">{index + 1}.</span>
                      <span className="stats-leader-name">{player.playerName}</span>
                      <span className="stats-leader-score">
                        {player.score} {scoreUnitLabel(latestSavedGame.gameType)}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="stats-history-note" style={{ marginTop: '0.75rem' }}>
                  Finished {new Date(latestSavedGame.playedAt).toLocaleString()}
                </div>
              </div>
            ) : (
              <div className="no-question">
                <span className="no-question-icon">💤</span>
                <span>
                  {gameState === 'waiting' ? 'Waiting for game to start' :
                   gameState === 'destroyed' ? 'Room ended with no saved game statistics' :
                   'No active question'}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Question Feedback Panel - This Room */}
        {roomFeedback.length > 0 && (
          <div className="detail-panel feedback-panel">
            <div className="panel-header">
              <h3 className="panel-title">
                <span className="title-icon">📋</span>
                Question Feedback (this room)
              </h3>
            </div>
            <div className="panel-content">
              <div className="feedback-room-list">
                {roomFeedback.filter(e => e.liked === true).length > 0 && (
                  <div className="feedback-group">
                    <div className="feedback-group-title">👍 Liked</div>
                    {roomFeedback.filter(e => e.liked === true).map((entry) => (
                      <div key={entry.id} className="feedback-room-item">
                        <div className="feedback-room-question">{(entry.questionText || '').slice(0, 80)}{(entry.questionText?.length || 0) > 80 ? '…' : ''}</div>
                        <div className="feedback-room-meta">
                          <span className="feedback-room-player">{(entry.playerName && entry.playerName.trim().length > 0) ? entry.playerName : (entry.playerId || '—')}</span>
                          {' · '}
                          {entry.rating != null && <span className="feedback-rating">{'⭐'.repeat(entry.rating)} {entry.rating} star{entry.rating !== 1 ? 's' : ''} given</span>}
                          {entry.rating != null && ' · '}
                          {new Date(entry.timestamp).toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {roomFeedback.filter(e => e.correct === false).length > 0 && (
                  <div className="feedback-group">
                    <div className="feedback-group-title">❌ Incorrect</div>
                    {roomFeedback.filter(e => e.correct === false).map((entry) => (
                      <div key={entry.id} className="feedback-room-item">
                        <div className="feedback-room-question">{(entry.questionText || '').slice(0, 80)}{(entry.questionText?.length || 0) > 80 ? '…' : ''}</div>
                        <div className="feedback-room-meta">
                          <span className="feedback-room-player">{(entry.playerName && entry.playerName.trim().length > 0) ? entry.playerName : (entry.playerId || '—')}</span>
                          {' · '}
                          {new Date(entry.timestamp).toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {roundFeedback.length > 0 && (
          <div className="detail-panel feedback-panel">
            <div className="panel-header">
              <h3 className="panel-title">
                <span className="title-icon">📝</span>
                Player Feedback (this room)
              </h3>
            </div>
            <div className="panel-content">
              <div className="feedback-room-list">
                {roundFeedback.map((entry) => (
                  <div key={entry.id} className="feedback-room-item">
                    {entry.questionText && (
                      <div className="feedback-room-question">
                        {(entry.questionText || '').slice(0, 80)}
                        {(entry.questionText?.length || 0) > 80 ? '…' : ''}
                      </div>
                    )}
                    <div className="feedback-room-meta">
                      <span className="feedback-room-player">
                        {(entry.playerName && entry.playerName.trim().length > 0)
                          ? entry.playerName
                          : (entry.playerId || '—')}
                      </span>
                      {' · '}
                      <span>{entry.message}</span>
                      {' · '}
                      {new Date(entry.timestamp).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Activity Log Panel - Full Width at Bottom */}
        <div className="detail-panel activity-panel">
          <div className="panel-header">
            <h3 className="panel-title">
              <span className="title-icon">📝</span>
              Live Activity
            </h3>
          </div>
          <div className="panel-content">
            <div className="activity-log">
              {/* Room creation/destruction info for historical rooms */}
              {room.destroyedAt && (
                <>
                  <div className="activity-item event-created">
                    <span className="activity-time">
                      {room.createdAt ? formatTimestamp(room.createdAt) : 'Unknown'}
                    </span>
                    <span className="activity-message">
                      <span className="event-icon">✨</span>
                      Room <strong>{room.roomName}</strong> was created
                      {room.gameType && (
                        <span className="event-detail"> ({GAME_TYPE_LABELS[room.gameType] || room.gameType})</span>
                      )}
                    </span>
                  </div>
                  {(displayQuestionsPlayed !== null || hasSavedStats) && (
                    <div className="activity-item">
                      <span className="activity-time">—</span>
                      <span className="activity-message">
                        <span className="event-icon">❓</span>
                        <strong>{displayQuestionsPlayed ?? savedStats.questionsPlayed}</strong> question{(displayQuestionsPlayed ?? savedStats.questionsPlayed) !== 1 ? 's' : ''} played
                        {displayGamesPlayed > 0 && (
                          <span className="event-detail"> across {displayGamesPlayed} game{displayGamesPlayed !== 1 ? 's' : ''}</span>
                        )}
                      </span>
                    </div>
                  )}
                  <div className="activity-item event-destroyed">
                    <span className="activity-time">
                      {formatTimestamp(room.destroyedAt)}
                    </span>
                    <span className="activity-message">
                      <span className="event-icon">🔚</span>
                      Room was destroyed
                      {displayedPlayers.length > 0 && (
                        <span className="event-detail"> ({displayedPlayers.length} player{displayedPlayers.length !== 1 ? 's' : ''} total)</span>
                      )}
                    </span>
                  </div>
                </>
              )}
              
              {/* Current room state */}
              {!room.destroyedAt && (
                <div className="activity-item">
                  <span className="activity-time">Now</span>
                  <span className="activity-message">
                    Room state: <strong style={{ color: stateConfig.color }}>{gameState.toUpperCase()}</strong>
                  </span>
                </div>
              )}
              
              {/* Game history events (room log, or synthesized from saved statistics) */}
              {activityEvents.slice().reverse().map((event, index) => (
                <div key={`event-${index}`} className={`activity-item ${event.type === 'game_started' ? 'event-started' : event.type === 'game_ended' ? 'event-ended' : ''}`}>
                  <span className="activity-time">
                    {formatTimestamp(event.timestamp)}
                  </span>
                  <span className="activity-message">
                    {event.type === 'game_started' && (
                      <>
                        <span className="event-icon">🎮</span>
                        Started <strong>{GAME_TYPE_LABELS[event.gameType || ''] || event.gameType}</strong>
                        {event.totalQuestions && <span className="event-detail"> ({event.totalQuestions} questions)</span>}
                      </>
                    )}
                    {event.type === 'game_ended' && (
                      <>
                        <span className="event-icon">{event.reason === 'completed' ? '🏆' : '⏹️'}</span>
                        {event.reason === 'completed' ? 'Completed' : 'Exited'} <strong>{GAME_TYPE_LABELS[event.gameType || ''] || event.gameType}</strong>
                        {event.reason === 'exited' && event.questionNumber && event.totalQuestions && (
                          <span className="event-detail"> (at question {event.questionNumber}/{event.totalQuestions})</span>
                        )}
                        {event.reason === 'completed' && event.questionNumber && event.totalQuestions && (
                          <span className="event-detail"> (completed all {event.totalQuestions} questions)</span>
                        )}
                        {event.leaderboard && event.leaderboard.length > 0 && (
                          <span className="event-detail">
                            {' — '}
                            {event.leaderboard
                              .slice()
                              .sort((a, b) => b.score - a.score)
                              .slice(0, 4)
                              .map((e) => `${e.playerName}: ${e.score}${isDrinkBasedGame(event.gameType) ? ' drinks' : ''}`)
                              .join(', ')}
                            {event.leaderboard.length > 4 ? '…' : ''}
                          </span>
                        )}
                      </>
                    )}
                    {event.type === 'player_joined' && (
                      <>
                        <span className="event-icon">👋</span>
                        <span className="player-mention">{event.playerName}</span> joined the room
                      </>
                    )}
                    {event.type === 'player_left' && (
                      <>
                        <span className="event-icon">👋</span>
                        <span className="player-mention">{event.playerName}</span> left the room
                      </>
                    )}
                  </span>
                </div>
              ))}
              
              {/* Player join events (from player data) - only show if no game history or for active rooms */}
              {(!room.gameHistory || room.gameHistory.length === 0 || !room.destroyedAt) && room.players.length > 0 && (
                <>
                  {room.players.slice().sort((a, b) => a.joinedAt - b.joinedAt).slice(-10).map((player) => (
                    <div key={player.id} className="activity-item">
                      <span className="activity-time">
                        {formatTimestamp(player.joinedAt)}
                      </span>
                      <span className="activity-message">
                        <span className="event-icon">👋</span>
                        <span className="player-mention">{getDisplayAvatar(player.avatar)} {player.name}</span> joined the room
                        {player.score > 0 && (
                          <span className="event-detail"> (score: {player.score})</span>
                        )}
                      </span>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomDetail;
